import {
  CATEGORY_LABELS_IT,
  SUBCATEGORY_LABELS_IT,
} from './ParameterCategories';
import {
  PARAMETERS_CATEGORY,
  PARAMETERS_GENERAL_NOTION,
  PARAMETERS_NAMES,
  PARAMETERS_SUB_CATEGORY,
} from './ParametersMetadata';

export type ParameterData = {
  value: number;
  unit: string;
  date: string;
  labName: string;
  range: { min: number; max: number };
  name: string;
  isOutdated?: boolean;
};

// Constants for threshold calculations
export const THRESHOLD_CONSTANTS = {
  WARNING_MARGIN_PERCENTAGE: 15, // 15% margin for warning zone
  EXTRA_PADDING_PERCENTAGE: 20, // 20% padding for extended warning zone
  LEGEND_GAP_PERCENT: 2, // 2% gap between legend zones
} as const;

// Add color constants
export const PARAMETER_COLORS = {
  normal: 'var(--success)',
  warning: 'var(--warning)',
  critical: 'var(--danger)',
  outdated: 'var(--outdated)',
} as const;

// Add zone labels
export const PARAMETER_ZONE_LABELS = {
  criticalHigh: 'Alto',
  warningHigh: 'Liev. alto',
  normal: 'Nella norma',
  warningLow: 'Liev. basso',
  criticalLow: 'Basso',
} as const;

export type DetailedParameterStatus =
  | 'normal'
  | 'critical-high'
  | 'critical-low'
  | 'warning-high'
  | 'warning-low'
  | 'outdated';

export type SimpleParameterStatus =
  | 'normal'
  | 'critical'
  | 'warning'
  | 'outdated';

export type SingleParameterStatus = 'normal' | 'high' | 'low' | 'outdated';
// Helper type for threshold direction
export type ThresholdDirection = 'high' | 'low';

// Utility functions to work with the statuses
export const parameterStatusUtils = {
  // Convert detailed status to simple status
  simplifyStatus: (status: DetailedParameterStatus): SimpleParameterStatus => {
    if (status === 'normal' || status === 'outdated') return status;
    if (status.startsWith('critical')) return 'critical';
    if (status.startsWith('warning')) return 'warning';
    return 'normal';
  },

  // Get direction for critical or warning status
  getDirection: (
    status: DetailedParameterStatus,
  ): ThresholdDirection | null => {
    if (status === 'normal' || status === 'outdated') return null;
    return status.endsWith('high') ? 'high' : 'low';
  },

  // Helper to check if a status is critical
  isCritical: (status: DetailedParameterStatus): boolean =>
    status.startsWith('critical'),

  // Helper to check if a status is warning
  isWarning: (status: DetailedParameterStatus): boolean =>
    status.startsWith('warning'),

  // Improved threshold calculations
  calculateWarningThresholds: (
    criticalLow: number,
    criticalHigh: number,
  ): { warningLow: number; warningHigh: number } => {
    const range = criticalHigh - criticalLow;
    const margin =
      (range * THRESHOLD_CONSTANTS.WARNING_MARGIN_PERCENTAGE) / 100;

    return {
      warningLow: criticalLow + margin,
      warningHigh: criticalHigh - margin,
    };
  },

  // Improved status calculation with automatic warning thresholds
  calculateStatus: (
    value: number,
    criticalLow: number,
    criticalHigh: number,
    date: string,
  ): { status: DetailedParameterStatus, outdated: boolean } => {
    let outdated = false
    // parameter is outdated if date is more than 6 months ago
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    if (new Date(date) < sixMonthsAgo) {
      outdated = true
      return { status: 'outdated', outdated }
    }

    // First check if the value is outside the critical range
    if (value <= criticalLow) return { status: 'critical-low', outdated };
    if (value >= criticalHigh) return { status: 'critical-high', outdated };

    // If not critical, calculate warning thresholds
    const { warningLow, warningHigh } =
      parameterStatusUtils.calculateWarningThresholds(
        criticalLow,
        criticalHigh,
      );

    // Then check if it's in the warning zone
    if (value <= warningLow) return { status: 'warning-low', outdated };
    if (value >= warningHigh) return { status: 'warning-high', outdated };
    
    return { status: 'normal', outdated };
  },

  // New utility to get threshold information
  getThresholdInfo: (criticalLow: number, criticalHigh: number) => {
    const { warningLow, warningHigh } =
      parameterStatusUtils.calculateWarningThresholds(
        criticalLow,
        criticalHigh,
      );

    return {
      criticalLow,
      warningLow,
      warningHigh,
      criticalHigh,
      normalRange: {
        min: warningLow,
        max: warningHigh,
      },
    };
  },
  // Add helper functions for status styling
  getColorForStatus: (status: DetailedParameterStatus): string => {
    switch (status) {
      case 'normal':
        return 'bg-success';
      case 'warning-high':
      case 'warning-low':
        return 'bg-warning';
      // return 'bg-success';
      case 'critical-high':
      case 'critical-low':
        return 'bg-danger';
      case 'outdated':
        return 'bg-outdated';
      default:
        return 'bg-gray-500';
    }
  },

  getLabelForStatus: (status: DetailedParameterStatus): string => {
    switch (status) {
      case 'normal':
        return 'Nella norma';
      case 'warning-high':
        return 'Livello Alto';
      case 'warning-low':
        return 'Livello Basso';
      case 'critical-high':
        return 'Alto';
      case 'critical-low':
        return 'Basso';
      case 'outdated':
        return 'Da aggiornare';
      default:
        return 'Unknown';
    }
  },

  getGenericLabelForStatus: (status: DetailedParameterStatus): string => {
    switch (status) {
      case 'normal':
        return 'Nella norma';
      case 'warning-high':
      case 'warning-low':
        return 'Vicino al limite';
      case 'critical-low':
      case 'critical-high':
        return 'Fuori norma';
      case 'outdated':
        return 'Da aggiornare';
      default:
        return 'Unknown';
    }
  },

  getLabelForSingleParameterStatus: (status: SingleParameterStatus, outdated: boolean): string => {
    let label = ""
    switch (status) {
      case 'normal':
        label = 'Nella norma';
        break;
      case 'high':
        label = 'Alto';
        break;
      case 'low':
        label = 'Basso';
        break;
      case 'outdated':
        label = 'Da aggiornare'
        break;
      default:
        label = 'Unknown';
    }
    if (outdated) label = label + " - Da aggiornare"
    return label
  },

  getColorForSingleParameterStatus: (status: SingleParameterStatus, outdated: boolean): string => {
    if (outdated) return 'bg-outdated'
    switch (status) {
      case 'normal':
        return 'bg-success';
      case 'high':
      case 'low':
        return 'bg-danger';
      default:
        return 'bg-gray-500';
    }
  },

  getSingleParameterStatus: (
    value: number,
    range: { min: number; max: number },
    date: string,
  ): { status: SingleParameterStatus, outdated: boolean } => {
    let outdated = false
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    if (new Date(date) < sixMonthsAgo) outdated = true;
    if (value >= range.max) return { status: 'high', outdated };
    if (value <= range.min) return { status: 'low', outdated };
    return { status: 'normal', outdated };
  },

  // Add these new functions for SimpleParameterStatus
  getColorForSimpleStatus: (status: SimpleParameterStatus): string => {
    switch (status) {
      case 'normal':
        return 'bg-parameter-normal';
      case 'warning':
        return 'bg-parameter-warning';
      case 'critical':
        return 'bg-parameter-critical';
      case 'outdated':
        return 'bg-parameter-outdated';
      default:
        return 'bg-gray-500';
    }
  },

  getGenericLabelForSimpleStatus: (status: SimpleParameterStatus): string => {
    switch (status) {
      case 'normal':
        return 'Nella norma';
      case 'warning':
        return 'Al limite';
      case 'critical':
        return 'Fuori norma';
      case 'outdated':
        return 'Da aggiornare';
      default:
        return 'Unknown';
    }
  },

  filterDataByStatus: (
    data: ParameterData[],
    targetStatus: SimpleParameterStatus,
  ) => {
    return data.filter((param) => {
      const { status } = parameterStatusUtils.calculateStatus(
        param.value,
        param.range.min,
        param.range.max,
        param.date,
      );
      const simplifiedStatus = parameterStatusUtils.simplifyStatus(status);
      return simplifiedStatus === targetStatus;
    });
  },
  aggregateParametersByName: (parameters: ParameterData[]): ParameterData[] => {
    // Group parameters by name
    const groupedParams = parameters.reduce((acc, param) => {
      if (!acc[param.name]) {
        acc[param.name] = [];
      }
      acc[param.name].push(param);
      return acc;
    }, {} as Record<string, ParameterData[]>);

    // Calculate aggregates for each group
    return Object.entries(groupedParams).map(([name, params]) => {
      // Get the latest parameter
      const latestParam = params.reduce((latest, current) => {
        return new Date(current.date) > new Date(latest.date)
          ? current
          : latest;
      });

      // Calculate average range from all measurements
      const avgRange = params.reduce(
        (acc, param) => ({
          min: acc.min + param.range.min,
          max: acc.max + param.range.max,
        }),
        { min: 0, max: 0 }
      );
      avgRange.min = avgRange.min / params.length;
      avgRange.max = avgRange.max / params.length;

      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const isOutdated = new Date(latestParam.date) < sixMonthsAgo;

      return {
        ...latestParam,
        range: avgRange, // Use the average range instead of the latest range
        isOutdated,
      };
    });
  },
  getColorForValue: (value: number, min: number, max: number): string => {
    if (value < min || value > max) return PARAMETER_COLORS.critical;

    const range = max - min;
    const position = (value - min) / range;
    const warningThreshold =
      THRESHOLD_CONSTANTS.WARNING_MARGIN_PERCENTAGE / 100;

    if (position < warningThreshold || position > 1 - warningThreshold) {
      return PARAMETER_COLORS.warning;
    }

    return PARAMETER_COLORS.normal;
  },
  getNameForId: (id: string): string => {
    return PARAMETERS_NAMES[id];
  },
  getGeneralNotionForId: (id: string): string => {
    return PARAMETERS_GENERAL_NOTION[id];
  },

  getParameterClassification(parameter: string): {
    category: string;
    subCategory: string | null;
  } {
    const category = PARAMETERS_CATEGORY[parameter];
    const subCategory = PARAMETERS_SUB_CATEGORY[parameter] ?? null;

    return { category, subCategory };
  },

  getItalianLabels(parameter: string): {
    categoryLabel: string | null;
    subCategoryLabel: string | null;
  } {
    const { category, subCategory } =
      parameterStatusUtils.getParameterClassification(parameter);

    return {
      categoryLabel: category ? CATEGORY_LABELS_IT[category] : null,
      subCategoryLabel: subCategory ? SUBCATEGORY_LABELS_IT[subCategory] : null,
    };
  },
};
